import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthProvider";
import "./recovery.css";

export function LoginPage() {
  const { configured, profile, requestPasswordReset, session, signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [recovering, setRecovering] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  if (!configured) return <Navigate replace to="/configuration" />;
  if (session && profile) return <Navigate replace to="/" />;

  async function submitLogin(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const result = await signIn(email, password);
    setSubmitting(false);
    if (result) setError(result);
    else navigate((location.state as { from?: string } | null)?.from ?? "/", { replace: true });
  }

  async function submitRecovery(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setMessage(null);
    const result = await requestPasswordReset(email);
    setSubmitting(false);
    if (result) setError(result);
    else setMessage("Si la cuenta existe, recibirás un correo con los pasos para continuar.");
  }

  if (recovering) {
    return (
      <section className="auth-page">
        <form className="auth-card" onSubmit={submitRecovery}>
          <p className="eyebrow">Recuperación de acceso</p>
          <h2>Recuperar contraseña</h2>
          <p>Te enviaremos un enlace seguro para establecer una contraseña nueva.</p>
          <label>
            Correo electrónico
            <input
              autoComplete="email"
              onChange={(event) => setEmail(event.target.value)}
              required
              type="email"
              value={email}
            />
          </label>
          {error && <p className="form-error" role="alert">{error}</p>}
          {message && <p className="form-success" role="status">{message}</p>}
          <button disabled={submitting || Boolean(message)} type="submit">
            {submitting ? "Enviando…" : "Enviar enlace de recuperación"}
          </button>
          <div className="auth-actions">
            <button
              className="auth-link"
              onClick={() => {
                setRecovering(false);
                setError(null);
                setMessage(null);
              }}
              type="button"
            >
              Volver al inicio de sesión
            </button>
          </div>
        </form>
      </section>
    );
  }

  return (
    <section className="auth-page">
      <form className="auth-card" onSubmit={submitLogin}>
        <p className="eyebrow">Acceso restringido</p>
        <h2>Ingresar al portal</h2>
        <p>Utiliza la cuenta proporcionada por la administración del proyecto.</p>
        <label>
          Correo electrónico
          <input
            autoComplete="email"
            onChange={(event) => setEmail(event.target.value)}
            required
            type="email"
            value={email}
          />
        </label>
        <label>
          Contraseña
          <input
            autoComplete="current-password"
            onChange={(event) => setPassword(event.target.value)}
            required
            type="password"
            value={password}
          />
        </label>
        {error && <p className="form-error" role="alert">{error}</p>}
        <button disabled={submitting} type="submit">
          {submitting ? "Ingresando…" : "Ingresar"}
        </button>
        <div className="auth-actions">
          <button
            className="auth-link"
            onClick={() => {
              setRecovering(true);
              setError(null);
            }}
            type="button"
          >
            ¿Olvidaste tu contraseña?
          </button>
        </div>
      </form>
    </section>
  );
}
